import { rateUnitLabels } from '@dreams-built/shared/src/quotes/types';
import { createFileRoute } from '@tanstack/react-router';
import { usePublicQuote, useRespondToQuoteMutation } from 'api/quoting';
import { COMPANY } from 'features/Landing/content';
import { Container, PhoneLink } from 'features/Landing/primitives';
import { useState, type FC } from 'react';

const formatNzd = (value: number) =>
  new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(value);

const StatusBanner: FC<{ status: string; expired: boolean }> = ({ status, expired }) => {
  if (status === 'ACCEPTED')
    {return (
      <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        You&rsquo;ve accepted this quote. We&rsquo;ll be in touch shortly to lock in dates.
      </div>
    );}
  if (status === 'DECLINED')
    {return (
      <div className="rounded-md border border-ink-200 bg-ink-100 px-4 py-3 text-sm text-ink-700">
        You declined this quote. Changed your mind or want it revised? Call{' '}
        <PhoneLink className="font-semibold" />.
      </div>
    );}
  if (expired)
    {return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        This quote has passed its validity date. Call <PhoneLink className="font-semibold" /> and
        we&rsquo;ll refresh it for you.
      </div>
    );}
  return null;
};

const PublicQuoteContent: FC<{ token: string }> = ({ token }) => {
  const { data: quote, isLoading, isError } = usePublicQuote(token);
  const respond = useRespondToQuoteMutation();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  if (isLoading) {
    return (
      <p className="py-24 text-center font-mono text-sm text-ink-500">Loading your quote…</p>
    );
  }

  if (isError || !quote) {
    return (
      <div className="py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-ink-900">Quote not found</h1>
        <p className="mt-3 text-ink-600">
          This link doesn&rsquo;t match a current quote. Call us on{' '}
          <PhoneLink className="font-semibold text-brand-600" /> and we&rsquo;ll sort it out.
        </p>
      </div>
    );
  }

  const canRespond = quote.status === 'SENT' && !quote.expired;

  return (
    <div className="py-12 sm:py-16">
      <div className="rounded-lg border border-ink-200 bg-white p-6 sm:p-10">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-ink-200 pb-6">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] text-ink-500 uppercase">
              Quote #{quote.quoteNumber}
            </p>
            <h1 className="font-display mt-2 text-2xl font-bold text-ink-900 sm:text-3xl">
              {quote.title || 'Your quote'}
            </h1>
            {quote.siteAddress ? (
              <p className="mt-1 text-sm text-ink-600">{quote.siteAddress}</p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="font-mono text-xs tracking-[0.2em] text-ink-500 uppercase">Total incl. GST</p>
            <p className="font-display text-3xl font-bold text-brand-600">{formatNzd(quote.total)}</p>
            {quote.validUntil ? (
              <p className="mt-1 font-mono text-xs text-ink-500">
                valid until {new Date(quote.validUntil).toLocaleDateString('en-NZ')}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4">
          <StatusBanner status={quote.status} expired={quote.expired} />
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left font-mono text-[11px] tracking-[0.15em] text-ink-500 uppercase">
                <th className="py-2 pr-4 font-medium">Item</th>
                <th className="py-2 pr-4 text-right font-medium">Qty</th>
                <th className="py-2 pr-4 font-medium">Unit</th>
                <th className="py-2 pr-4 text-right font-medium">Rate</th>
                <th className="py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {quote.lineItems
                .filter((item) => item.qty > 0 || item.total > 0)
                .map((item) => (
                  <tr key={item.id}>
                    <td className="py-2.5 pr-4 text-ink-800">{item.description}</td>
                    <td className="py-2.5 pr-4 text-right text-ink-700">{item.qty}</td>
                    <td className="py-2.5 pr-4 text-ink-500">{rateUnitLabels[item.unit]}</td>
                    <td className="py-2.5 pr-4 text-right text-ink-700">{formatNzd(item.rate)}</td>
                    <td className="py-2.5 text-right font-medium text-ink-900">
                      {formatNzd(item.total)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <dl className="w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-600">Subtotal</dt>
              <dd className="text-ink-900">{formatNzd(quote.subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-600">GST ({Math.round(quote.gstRate * 100)}%)</dt>
              <dd className="text-ink-900">{formatNzd(quote.gst)}</dd>
            </div>
            <div className="flex justify-between border-t border-ink-200 pt-2">
              <dt className="font-semibold text-ink-900">Total</dt>
              <dd className="font-display text-lg font-bold text-ink-900">{formatNzd(quote.total)}</dd>
            </div>
          </dl>
        </div>

        {quote.notes ? (
          <div className="mt-8 rounded-md bg-ink-50 p-4">
            <p className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">Notes &amp; terms</p>
            <p className="mt-2 text-sm whitespace-pre-wrap text-ink-700">{quote.notes}</p>
          </div>
        ) : null}

        {canRespond ? (
          <div className="mt-10 border-t border-ink-200 pt-8">
            {declining ? (
              <div>
                <label htmlFor="decline-reason" className="mb-1.5 block text-sm font-medium text-ink-800">
                  Anything we should know? (optional)
                </label>
                <textarea
                  id="decline-reason"
                  rows={3}
                  className="w-full rounded-md border border-ink-300 px-4 py-3 text-sm"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <div className="mt-3 flex gap-3">
                  <button
                    type="button"
                    disabled={respond.isPending}
                    onClick={() => respond.mutate({ token, accept: false, reason })}
                    className="rounded-md bg-ink-800 px-6 py-3 text-sm font-semibold text-white hover:bg-ink-900"
                  >
                    Confirm decline
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeclining(false)}
                    className="rounded-md px-6 py-3 text-sm font-semibold text-ink-700"
                  >
                    Back
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  disabled={respond.isPending}
                  onClick={() => respond.mutate({ token, accept: true })}
                  className="rounded-md bg-brand-600 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
                >
                  Accept this quote
                </button>
                <button
                  type="button"
                  onClick={() => setDeclining(true)}
                  className="rounded-md px-6 py-4 text-sm font-semibold text-ink-600 hover:text-ink-900"
                >
                  Decline
                </button>
                <p className="text-xs text-ink-500 sm:ml-auto sm:max-w-[220px]">
                  Questions first? Call <PhoneLink className="font-semibold" />, {COMPANY.hours}.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const PublicQuotePage: FC = () => {
  // TanStack's file-route pattern is circular by design: Route is read at render
  // time, long after module init.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const { token } = Route.useParams();
  return (
    <section className="min-h-[70vh] bg-[#f2f3f1] pt-24">
      <Container className="max-w-3xl">
        <PublicQuoteContent token={token} />
      </Container>
    </section>
  );
};

export const Route = createFileRoute('/_landing/q/$token')({
  ssr: false,
  head: () => ({
    meta: [
      { title: 'Your quote | Dreams Built' },
      { name: 'robots', content: 'noindex' }
    ]
  }),
  component: PublicQuotePage
});
